import React, { useMemo } from 'react';

export const PointSeriesWeekSummary = ({ week, schedule, navigate }) => {
    const weekInfo = useMemo(() => schedule.weekInfo.find(wi => wi.week === week.week), [schedule.weekInfo, week.week]);

    // Handle legacy and current property names for robustness
    const isDoubleElim = weekInfo?.doubleElim === true || weekInfo?.deChecked === true;
    const isRoundRobin = weekInfo?.roundRobin === true || weekInfo?.rrChecked === true;
    const isSingles = weekInfo?.singles === true;
    const isDoubles = weekInfo?.doubles === true;

    const tournamentType = useMemo(() => {
        if (!weekInfo) return 'N/A';
        const style = isDoubleElim ? 'Double Elimination' : (isRoundRobin ? 'Round Robin' : '');
        const format = isSingles ? 'Singles' : (isDoubles ? 'Doubles' : '');
        
        if (style && format) {
            return `${style} / ${format}`;
        }
        return style || format || '$10 Blind Draw @ Down Lo'; // Fallback
    }, [weekInfo, isDoubleElim, isRoundRobin, isSingles, isDoubles]);
    
    const playersThisWeek = useMemo(() => (schedule.players || [])
        .map(p => ({ ...p, entry: p.entries.find(e => e.week === week.week) }))
        .filter(p => p.entry)
        .sort((a, b) => (b.entry.points || 0) - (a.entry.points || 0)), [schedule.players, week.week]);
    
    const { highestOutPlayerName, highestOutValue } = useMemo(() => {
        if (!playersThisWeek || playersThisWeek.length === 0) return { highestOutPlayerName: null, highestOutValue: 0 };
        let maxOut = 0;
        let topPlayerName = null;
        for (const p of playersThisWeek) {
            const out = p.entry?.highOut;
            if (typeof out === 'number' && out > maxOut) {
                maxOut = out;
                topPlayerName = p.name;
            }
        }
        return { highestOutPlayerName: topPlayerName, highestOutValue: maxOut };
    }, [playersThisWeek]);

    const mysteryOutNumber = useMemo(() => {
        const mysteryEntry = schedule.mysteryOut?.find(mo => mo.week === week.week);
        return mysteryEntry?.mysteryOut;
    }, [schedule.mysteryOut, week.week]);

    const isFutureEvent = useMemo(() => {
        if (!week.date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eventDate = new Date(week.date);
        return eventDate > today;
    }, [week.date]);

    const groupedPlayers = useMemo(() => {
        if (!isDoubles) { // Now uses the explicit boolean
            return playersThisWeek.map(p => ({ type: 'single', players: [p], entry: p.entry }));
        }

        const result = [];
        const processedPlayerIds = new Set();
        const playersById = new Map(playersThisWeek.map(p => [p.id, p]));

        // --- Pass 1: Group players with an explicit partnerId ---
        for (const player of playersThisWeek) {
            if (processedPlayerIds.has(player.id)) continue;

            const partnerId = player.entry?.partnerId;
            const partner = partnerId ? playersById.get(partnerId) : undefined;

            if (partner && !processedPlayerIds.has(partner.id)) {
                // Found an explicit partner who hasn't been grouped yet.
                result.push({ type: 'pair', players: [player, partner], entry: player.entry });
                processedPlayerIds.add(player.id);
                processedPlayerIds.add(partner.id);
            }
        }

        // --- Pass 2: Fallback to point-matching for any remaining players ---
        const remainingPlayers = playersThisWeek.filter(p => !processedPlayerIds.has(p.id));
        for (const player of remainingPlayers) {
            if (processedPlayerIds.has(player.id)) continue;

            // Find a partner among the *remaining* players with the same score
            const partner = remainingPlayers.find(
                p => p.id !== player.id && !processedPlayerIds.has(p.id) && p.entry.points === player.entry.points
            );

            if (partner) {
                result.push({ type: 'pair', players: [player, partner], entry: player.entry });
                processedPlayerIds.add(player.id);
                processedPlayerIds.add(partner.id);
            } else {
                // If no partner found even with point-matching, it's a single entry
                result.push({ type: 'single', players: [player], entry: player.entry });
                processedPlayerIds.add(player.id);
            }
        }

        return result;
    }, [playersThisWeek, isDoubles]);

    if (playersThisWeek.length === 0) {
        return (
            <div className="card rounded-lg p-4 md:p-6 text-center">
                <h4 className="text-xl font-bold heading-primary-2 mb-3">{tournamentType}</h4>
                <p className="text-muted mt-4">
                    {isFutureEvent ? (
                        <>
                            This is an upcoming event
                            <br />
                            Tournament type TBD
                            <br />
                            <a href="https://chicodarts.org/point-series" className="text-info hover:underline">
                                See Point Series Overview for more details.
                            </a>
                        </>
                    ) :  
                        <>
                            This is an upcoming event
                            <br />
                            Tournament type TBD
                            <br />
                            <a href="https://chicodarts.org/point-series" className="text-info hover:underline">
                                See Point Series Overview for more details.
                            </a>
                        </>}
                </p>
            </div>
        );
    }

    return (
        <div className="card rounded-lg p-4 md:p-6">
            <h4 className="text-xl font-bold heading-primary-2 mb-3 text-center">{tournamentType}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center mb-4 border-b border-low pb-4">
                <div className="p-2 rounded-md surface-low">
                    <div className="text-xs text-muted">1st Place</div>
                    <div className="font-bold text-strong">${weekInfo?.firstPlacePayout || 0}{weekInfo?.doubles ? <span className="text-xs font-normal"> /each</span> : ''}</div>
                </div>
                <div className="p-2 rounded-md surface-low">
                    <div className="text-xs text-muted">2nd Place</div>
                    <div className="font-bold text-strong">${weekInfo?.secondPlacePayout || 0}{weekInfo?.doubles ? <span className="text-xs font-normal"> /each</span> : ''}</div>
                </div>
                <div className="p-2 rounded-md surface-low">
                    <div className="text-xs text-muted">Mystery Out</div>
                    <div className="font-bold text-strong">{mysteryOutNumber || '?'}</div>
                </div>
                <div className="p-2 rounded-md surface-low">
                    <div className="text-xs text-muted">High Out</div>
                    <div className="font-bold text-strong truncate">{highestOutPlayerName ? `${highestOutPlayerName} (${highestOutValue})` : 'N/A'}</div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="table-header"><tr><th className="px-3 py-2 text-left">Player(s)</th><th className="px-3 py-2 text-center">Points</th></tr></thead>
                    <tbody>
                        {groupedPlayers.map((group, idx) => (
                            <tr key={idx} className="row-alt">
                                <td className="px-3 py-2 font-semibold text-strong">
                                    {group.players.map(p => p.name).join(' & ')}
                                </td>
                                <td className="px-3 py-2 text-center">{group.entry.points}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};